import type { RouteLocationNormalized } from 'vue-router';

export type NumericScrollPosition = { left: number; top: number };
export type RouterScrollPosition = NumericScrollPosition | { el: string } | undefined;

export function createDefaultScrollMemory(maxEntries = 50) {
  const positions = new Map<string, NumericScrollPosition>();

  return {
    remember(route: { fullPath: string; path: string }, position: NumericScrollPosition) {
      const normalized = normalizeScrollPosition(position);
      if (!normalized) return;
      for (const key of [route.fullPath, route.path]) {
        if (!key) continue;
        positions.delete(key);
        positions.set(key, normalized);
      }
      while (positions.size > maxEntries) positions.delete(positions.keys().next().value!);
    },
    position(route: { fullPath: string; path: string }) {
      return positions.get(route.fullPath) ?? positions.get(route.path);
    },
  };
}

export const defaultScrollMemory = createDefaultScrollMemory();

export function normalizeScrollPosition(position: NumericScrollPosition | null | undefined): NumericScrollPosition | undefined {
  if (!position || !Number.isFinite(position.left) || !Number.isFinite(position.top)) return undefined;
  return { left: Math.max(0, position.left), top: Math.max(0, position.top) };
}

export function resolveDefaultScroll(
  to: Pick<RouteLocationNormalized, 'hash' | 'meta'>,
  savedPosition: NumericScrollPosition | null,
  rememberedPosition?: NumericScrollPosition,
): RouterScrollPosition {
  const saved = normalizeScrollPosition(savedPosition);
  const remembered = normalizeScrollPosition(rememberedPosition);
  if (saved && (saved.left > 0 || saved.top > 0 || !remembered)) return saved;
  if (to.hash) return { el: to.hash };
  if (to.meta.scrollToTop) return { left: 0, top: 0 };
  return remembered ?? saved;
}
